import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Filter, Plus, Edit, Trash2, MapPin, Eye, XCircle, Loader2, Building2, Image as ImageIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../components/ui/Button';
import { StatCard } from '../../components/ui/StatCard';
import { PropertyService } from '../../handling_data/services/PropertyService';
import { PropertyProxy } from '../../handling_data/proxies/PropertyProxy';
import { MediaManagementModal } from './MediaManagementModal';
import { PropertyPreviewModal } from './PropertyPreviewModal';

export const PropertiesList: React.FC = () => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const [properties, setProperties] = useState<any[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(true);
    const [isMediaModalOpen, setIsMediaModalOpen] = useState(false);
    const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
    const [selectedPropertyId, setSelectedPropertyId] = useState<number | null>(null);

    const fetchProperties = React.useCallback(async () => {
        try {
            setLoading(true);
            const response = await PropertyService.getProperties();
            const propertiesArray = response.data || [];
            const formatted = PropertyProxy.formatListForUI(propertiesArray, i18n.language);
            setProperties(formatted);
        } catch (error) {
            console.error('Failed to fetch properties:', error);
        } finally {
            setLoading(false);
        }
    }, [i18n.language]);

    useEffect(() => {
        fetchProperties();
    }, [i18n.language, fetchProperties]);

    // Handle auto-refresh when media upload finishes
    useEffect(() => {
        const handleRefresh = (e: any) => {
            console.log('Media updated, refreshing list...', e.detail);
            fetchProperties();
        };
        window.addEventListener('property-media-updated', handleRefresh);
        return () => window.removeEventListener('property-media-updated', handleRefresh);
    }, [fetchProperties]);

    const handleDelete = async (id: number) => {
        if (window.confirm(t('common.confirm_delete') || 'هل أنت متأكد من الحذف؟')) {
            try {
                await PropertyService.deleteProperty(id);
                fetchProperties();
            } catch (error) {
                console.error('Failed to delete property:', error);
                alert('فشل حذف العقار. يرجى المحاولة مرة أخرى.');
            }
        }
    };

    const statusColors: Record<string, string> = {
        available: 'bg-green-100 text-green-700',
        rented: 'bg-blue-100 text-blue-700',
        maintenance: 'bg-orange-100 text-orange-700',
        sold: 'bg-gray-100 text-gray-700',
    };

    const openMediaModal = (id: number) => {
        setSelectedPropertyId(id);
        setIsMediaModalOpen(true);
    };

    const openPreviewModal = (id: number) => {
        setSelectedPropertyId(id);
        setIsPreviewModalOpen(true);
    };

    const filteredProperties = properties.filter(p =>
        p.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.address.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="space-y-6 animate-fade-in">
            {/* Page Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h2 className="text-2xl font-bold text-primary">{t('properties_page.title')}</h2>
                    <p className="text-v3-text-secondary text-sm">عرض وإدارة جميع العقارات المسجلة</p>
                </div>
                <Button
                    onClick={() => navigate('/properties/new')}
                    className="flex items-center gap-2 bg-primary text-white px-5 py-2.5 rounded-xl hover:bg-primary/90 transition-all shadow-lg shadow-primary/20"
                >
                    <Plus className="w-5 h-5" />
                    <span>{t('properties_page.add_new')}</span>
                </Button>
            </div>

            {/* Stats Row */}
            {!loading && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <StatCard
                        title={t('sidebar.properties')}
                        value={properties.length}
                        icon={Building2}
                        color="bg-blue-500"
                    />
                </div>
            )}

            {/* Filters & Search */}
            <div className="bg-white p-4 rounded-2xl shadow-sm border border-v3-border flex flex-col md:flex-row gap-4">
                <div className="relative flex-1">
                    <Search className={`absolute ${i18n.dir() === 'rtl' ? 'right-3' : 'left-3'} top-3 w-5 h-5 text-gray-400`} />
                    <input
                        type="text"
                        placeholder={t('properties_page.search_placeholder')}
                        className={`w-full ${i18n.dir() === 'rtl' ? 'pr-10 pl-4' : 'pl-10 pr-4'} py-2.5 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all`}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
                <button className="flex items-center gap-2 px-4 py-2.5 border border-gray-200 rounded-xl hover:bg-gray-50 text-gray-600 transition-colors">
                    <Filter className="w-5 h-5" />
                    <span>{t('properties_page.filter_status')}</span>
                </button>
            </div>

            {/* Properties Table */}
            <div className="bg-white rounded-2xl shadow-sm border border-v3-border overflow-hidden min-h-[400px] flex flex-col">
                {loading ? (
                    <div className="flex-1 flex flex-col items-center justify-center text-gray-400 gap-3">
                        <Loader2 className="w-10 h-10 animate-spin text-primary" />
                        <span>{t('common.loading')}...</span>
                    </div>
                ) : filteredProperties.length > 0 ? (
                    <div className="overflow-x-auto">
                        <table className="w-full text-right">
                            <thead className="bg-gray-50 border-b border-gray-100">
                                <tr>
                                    <th className={`py-4 px-6 font-semibold text-gray-600 text-sm ${i18n.dir() === 'rtl' ? 'text-right' : 'text-left'}`}>{t('properties_page.table.image')}</th>
                                    <th className={`py-4 px-6 font-semibold text-gray-600 text-sm ${i18n.dir() === 'rtl' ? 'text-right' : 'text-left'}`}>{t('properties_page.table.name')}</th>
                                    <th className={`py-4 px-6 font-semibold text-gray-600 text-sm ${i18n.dir() === 'rtl' ? 'text-right' : 'text-left'}`}>{t('properties_page.table.price')}</th>
                                    <th className={`py-4 px-6 font-semibold text-gray-600 text-sm ${i18n.dir() === 'rtl' ? 'text-right' : 'text-left'}`}>{t('properties_page.table.status')}</th>
                                    <th className={`py-4 px-6 font-semibold text-gray-600 text-sm ${i18n.dir() === 'rtl' ? 'text-right' : 'text-left'}`}>{t('properties_page.table.owner')}</th>
                                    <th className="py-4 px-6 font-semibold text-gray-600 text-sm text-center">{t('properties_page.table.actions')}</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {filteredProperties.map((property) => (
                                    <tr key={property.id} className="hover:bg-gray-50/50 transition-colors group">
                                        <td className="py-4 px-6">
                                            <div className="relative w-24 h-16 rounded-lg overflow-hidden shadow-sm">
                                                <img src={property.image} alt={property.title} className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-500" />
                                                {!property.is_active && (
                                                    <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                                                        <XCircle className="w-6 h-6 text-white" />
                                                    </div>
                                                )}
                                            </div>
                                        </td>
                                        <td className="py-4 px-6">
                                            <div className="font-bold text-gray-800 text-base">{property.title}</div>
                                            <div className="flex items-center gap-1 text-xs text-gray-500 mt-1">
                                                <MapPin className="w-3 h-3" />
                                                {property.address}
                                            </div>
                                        </td>
                                        <td className="py-4 px-6">
                                            <div className="flex flex-col gap-1">
                                                {property.price_daily && <span className="text-xs text-gray-600 flex items-center gap-1"><span className="font-bold text-primary">${property.price_daily}</span> / <span className="opacity-70">{t('common.day')}</span></span>}
                                                {property.price_monthly && <span className="text-sm text-gray-800 flex items-center gap-1"><span className="font-bold text-primary">${property.price_monthly}</span> / <span className="opacity-70">{t('common.month')}</span></span>}
                                            </div>
                                        </td>
                                        <td className="py-4 px-6">
                                            <span className={`px-3 py-1.5 rounded-full text-xs font-bold ring-1 ring-inset ${statusColors[property.status]} ring-opacity-20`}>
                                                {t(`properties_page.status.${property.status}`)}
                                            </span>
                                        </td>
                                        <td className="py-4 px-6">
                                            <div className="flex items-center gap-3">
                                                <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center text-primary text-sm font-bold border border-primary/20">
                                                    {(property.owner?.name || 'U').charAt(0)}
                                                </div>
                                                <div className="flex flex-col">
                                                    <span className="text-sm font-medium text-gray-700">{property.owner?.name}</span>
                                                    <span className="text-xs text-gray-400 dir-ltr text-right">{property.owner?.phone}</span>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="py-4 px-6">
                                            <div className="flex items-center justify-center gap-2 md:opacity-0 md:group-hover:opacity-100 transition-opacity">
                                                <button
                                                    onClick={() => openPreviewModal(property.id)}
                                                    className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 text-gray-600 rounded-lg transition-colors border border-transparent hover:border-gray-200"
                                                    title={t('common.view_details')}
                                                >
                                                    <Eye className="w-4 h-4" />
                                                </button>
                                                <button
                                                    onClick={() => openMediaModal(property.id)}
                                                    className="w-8 h-8 flex items-center justify-center hover:bg-purple-50 text-purple-600 rounded-lg transition-colors border border-transparent hover:border-purple-100"
                                                    title={t('property_form.steps.media') || 'إدارة الصور'}
                                                >
                                                    <ImageIcon className="w-4 h-4" />
                                                </button>
                                                <button
                                                    onClick={() => navigate(`/properties/edit/${property.id}`)}
                                                    className="w-8 h-8 flex items-center justify-center hover:bg-blue-50 text-blue-600 rounded-lg transition-colors border border-transparent hover:border-blue-100"
                                                    title={t('common.edit')}
                                                >
                                                    <Edit className="w-4 h-4" />
                                                </button>
                                                <button
                                                    onClick={() => handleDelete(property.id)}
                                                    className="w-8 h-8 flex items-center justify-center hover:bg-red-50 text-red-600 rounded-lg transition-colors border border-transparent hover:border-red-100"
                                                    title={t('common.delete')}
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className="flex-1 flex flex-col items-center justify-center text-gray-400 py-20">
                        <MapPin className="w-16 h-16 mb-4 opacity-10" />
                        <p className="text-lg">{t('home.no_properties')}</p>
                    </div>
                )}
            </div>

            {/* Media Management Modal */}
            <MediaManagementModal
                isOpen={isMediaModalOpen}
                onClose={() => setIsMediaModalOpen(false)}
                propertyId={selectedPropertyId}
            />

            <PropertyPreviewModal
                isOpen={isPreviewModalOpen}
                onClose={() => setIsPreviewModalOpen(false)}
                propertyId={selectedPropertyId}
            />
        </div>
    );
};
